import React, { useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import { Row, Col, Spin, Checkbox } from "antd";
import { PageHeader } from "../../../components/page-headers/page-headers";
import { Main, CardToolbox } from "../../styled";

import { Button } from "../../../components/buttons/buttons";
import { GetGraphQlData, GetCiselnikyZakaziek } from "./dataQueries";
import { PanelFiltrov } from "../../../components/filtre/panelFiltrov";

import { useTranslation } from "react-i18next";
import { AutoComplete } from "../../../components/autoComplete/autoComplete";

import {
  setAllServices,
  setTableFiler,
} from "../../../redux/services/actionCreator";

import List from "./list";
import { zUrl, doUrl } from "../../../utils/stavVUrl";
import { useZdrzanie, usePoZmene } from "../../../utils/zdrzanie";

import { useDispatch, useSelector } from "react-redux";
import NewRecordModal from "./newRecordModal";
import sendBusinessLeaseInvoices from "../../../Api/invoice";
import displayNotification from "../../reservation/admin/notification/notification";

const graphqlVariables = {
  tokenGet: process.env.REACT_APP_TOKEN_GET,
  tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
};

const POPIS_URL = {
  hladanie: "text",
  strana: "cislo",
  naStranu: "cislo",
  stav: "text",
  branchOfficeId: "cislo",
  carOwnerId: "cislo",
  carRenterId: "cislo",
  obdobie: "obdobie",
  suma: "rozsah",
  cisloFaktury: "text",
};

// STAVY ZAKAZKY ako filter nad zoznamom. Hore su tie, podla ktorych sa
// najcastejsie fakturuje. "Schvalene" pokryva aj rucne schvalene zakazky:
// na fakturaciu je to to iste a predtym sa pod tym filtrom neukazali.
const STAVY_ZAKAZIEK = [
  { value: "Otvorené", label: "open", name: "open" },
  { value: "Schválené", label: "approved", name: "approved,approved_manually" },
  { value: "Fakturované", label: "invoiced", name: "invoiced" },
  // Zakazky na zbernej fakture. Vlastny stav zamerne, aby bolo vidno,
  // ktore sli zbernou a ktore maju vlastnu fakturu.
  {
    value: "Zbernou FA",
    label: "invoiced_collective",
    name: "invoiced_collective",
  },
  { value: "BL Export", label: "blExported", name: "blExported" },
  { value: "Odoslané", label: "sent", name: "sent" },
];

const Actions = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const zoZaciatku = zUrl(location.search, POPIS_URL);

  const tableData = useSelector((state) => state?.services?.allServices);
  const listData = useSelector((state) => state?.services?.tableFilter);
  const [pageNumberToGrid, setPageNumberToGrid] = useState(zoZaciatku.strana || 1);
  const [loadingList, setLoadingList] = useState(true);
  const [total, setTotal] = useState(0);
  const [generateCsvData, setGenerateCsvData] = useState(false);
  const [gridState, setGridState] = useState({
    addNewRecord: false,
  });

  const [branchOficcesData, setBranchOficcesData] = useState([]);
  // Zaskrtnutie checkboxu sa obnovi z adresy. Predtym sa po obnoveni strany
  // filtrovalo podla stavu, ale ziadny checkbox nebol zaskrtnuty.
  const [value, setValue] = useState(
    STAVY_ZAKAZIEK.find((s) => s.name === zoZaciatku.stav)?.value || ""
  );
  const [checkedStatus, setCheckedStatus] = useState(zoZaciatku.stav);
  const [loadData, setLoadData] = useState(true);
  const [limit, setLimit] = useState(zoZaciatku.naStranu || 20);
  const [loadCsvData, setLoadCsvData] = useState(false);
  const [searchText, setSearchText] = useState(zoZaciatku.hladanie || "");
  const hladanyText = useZdrzanie(searchText, 300);

  const [filtre, setFiltre] = useState({
    branchOfficeId: zoZaciatku.branchOfficeId,
    carOwnerId: zoZaciatku.carOwnerId,
    carRenterId: zoZaciatku.carRenterId,
    obdobie: zoZaciatku.obdobie,
    suma: zoZaciatku.suma,
    cisloFaktury: zoZaciatku.cisloFaktury,
  });

  // Filtre, stav, hladanie a strana ziju v adrese, aby ich krok spat
  // nezahodil a aby sa dal vypis poslat odkazom.
  useEffect(() => {
    const novy = doUrl(
      {
        ...filtre,
        stav: checkedStatus || undefined,
        hladanie: hladanyText,
        strana: pageNumberToGrid > 1 ? pageNumberToGrid : undefined,
        naStranu: limit !== 20 ? limit : undefined,
      },
      POPIS_URL
    );
    if (novy !== location.search) {
      history.replace({ pathname: location.pathname, search: novy });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtre, checkedStatus, hladanyText, pageNumberToGrid, limit]);

  const obdobie = filtre.obdobie;
  // Prazdne policko znamena "neobmedzene", nie nulu.
  const cislo = (h) => {
    if (h === undefined || h === null || String(h).trim() === "") return undefined;
    const n = Number(h);
    return Number.isFinite(n) ? n : undefined;
  };

  // Create query variables object that includes all dependencies
  const queryVariables = {
    ...graphqlVariables,
    offset: (pageNumberToGrid - 1) * limit,
    // Stranovanie plati aj pri hladani. Predtym sa pri hladani limit zrusil
    // a server poslal vsetky zhody naraz, co pri kratkom vstupe zaseklo
    // prehliadac.
    limit: limit,
    status: checkedStatus || undefined,
    search: hladanyText || undefined,
    branchOfficeId: filtre.branchOfficeId,
    carOwnerId: filtre.carOwnerId,
    carRenterId: filtre.carRenterId,
    dateFrom: obdobie && obdobie[0] ? obdobie[0].format("YYYY-MM-DD") : undefined,
    dateTo: obdobie && obdobie[1] ? obdobie[1].format("YYYY-MM-DD") : undefined,
    sumaOd: cislo(filtre.suma && filtre.suma[0]),
    sumaDo: cislo(filtre.suma && filtre.suma[1]),
    cisloFaktury: filtre.cisloFaktury || undefined,
  };

  // Get the query result using the hook at component level
  const graphQlData = GetGraphQlData(queryVariables, loadData);

  if (loadCsvData === true) {
    const csvQueryVariables = {
      ...graphqlVariables,
      offset: 0,
      limit: 0,
      status: undefined,
      search: undefined,
    };
    const csvData = GetGraphQlData(csvQueryVariables, loadData);
    console.log("csvData", csvData);
  }

  useEffect(() => {
    if (graphQlData) {
      const servicesData = graphQlData[0]?.data?.getServiceActions;
      const branchOffices = graphQlData[1]?.data?.getBranchOficces;

      if (servicesData?.records && branchOffices) {
        setBranchOficcesData(branchOffices);
        setTotal(servicesData.total || 0);

        // Only update Redux state if the data has actually changed
        if (
          JSON.stringify(tableData) !== JSON.stringify(servicesData.records)
        ) {
          dispatch(setAllServices(servicesData.records));
          dispatch(setTableFiler(servicesData.records));
        }
        setLoadingList(false);
      }
    }
  }, [graphQlData, dispatch, tableData]);

  // Effect to handle page changes
  useEffect(() => {
    setLoadData(true);
  }, [pageNumberToGrid]);

  useEffect(() => {
    if (generateCsvData === true) {
      setLoadCsvData(true);
    }
  }, [generateCsvData]);

  const handleChangePage = (data) => {
    setLoadingList(true);
    setPageNumberToGrid(data?.pageNumber);
    setLimit(data?.pageSize);
  };
  const handleDownloadCsv = (status) => {
    setLoadingList(true);
    setGenerateCsvData(status);
  };

  // Bez podmienky na pocet znakov, aby aj jeden znak nasiel vsetky zhody.
  const handleSearch = (text) => {
    setSearchText(typeof text === "string" ? text : "");
  };

  // Az pri skutocnej zmene hladania, nie pri prvom vykresleni, inak by
  // sa strana z adresy hned po otvoreni zoznamu zhodila na jednotku.
  usePoZmene(hladanyText, () => {
    setPageNumberToGrid(1);
    setLoadingList(true);
    setLoadData(true);
  });

  const showNewRecordModal = () => {
    setGridState({
      ...gridState,
      addNewRecord: true,
    });
  };

  const exportBlAction = async () => {
    const confirm = window.confirm("Poslať hromadnú fakturáciu do BL?");
    if (confirm) {
      const result = await sendBusinessLeaseInvoices();
      let type = "success";
      let reload = true;
      const message = t("backend.notifications.update");
      let desc = "E-mail bol odoslaný";
      if (result.data.message === "no data") {
        desc = "Žiadne faktúry na odoslanie";
        reload = false;
      }
      if (result.status === false) {
        type = "error";
        desc = "E-mail nebol odoslaný, obráťte sa na podporu.";
        reload = false;
      }

      displayNotification(type, message, desc);
      if (reload) {
        setTimeout(function() {
          window.location.reload();
        }, 1000);
      }
    }
  };
  const onCancel = () => {
    setGridState({
      ...gridState,
      addNewRecord: false,
    });
  };
  const handleCancel = () => {
    onCancel();
  };

  const onChangeCheckbox = (e) => {
    let data = tableData;
    setValue("");
    if (e.target.checked === true) {
      // Stav moze byt aj viac stavov oddelenych ciarkou, napriklad schvalene
      // a schvalene rucne. Server to vie, tento predfilter to musi vediet tiez,
      // inak by zoznam do prichodu novych dat zostal prazdny.
      const stavy = String(e.target.name).split(",");
      data = tableData.filter((item) => stavy.includes(item.status));
      setValue(e.target.value);
      setCheckedStatus(e.target.name);
    } else {
      setValue("");
      setCheckedStatus("");
    }
    dispatch(setTableFiler(data));
  };

  const ciselniky = GetCiselnikyZakaziek(graphqlVariables, true);
  const majitelia = ciselniky[0]?.data?.getCarOwners;
  const najomcovia = ciselniky[1]?.data?.getCarRenters;

  const naMoznosti = (zoznam) =>
    (Array.isArray(zoznam) ? zoznam : [])
      .map((p) => ({
        hodnota: Number(p.id),
        popis: p.company || `${p.firstname || ""} ${p.lastname || ""}`.trim(),
      }))
      .filter((m) => m.popis)
      .sort((a, b) => a.popis.localeCompare(b.popis, "sk"));

  const definicieFiltrov = [
    {
      kluc: "branchOfficeId",
      popis: "Pneuservis",
      typ: "vyber",
      sirka: 4,
      moznosti: naMoznosti(branchOficcesData),
    },
    {
      kluc: "carOwnerId",
      popis: "Majiteľ",
      typ: "vyber",
      sirka: 5,
      moznosti: naMoznosti(majitelia),
    },
    {
      kluc: "carRenterId",
      popis: "Nájomca",
      typ: "vyber",
      sirka: 5,
      moznosti: naMoznosti(najomcovia),
    },
    { kluc: "obdobie", popis: "Dátum zákazky", typ: "obdobie", sirka: 6 },
    { kluc: "suma", popis: "Suma zákazky v eurách", typ: "rozsah", sirka: 4 },
    {
      kluc: "cisloFaktury",
      popis: "Číslo faktúry",
      typ: "text",
      sirka: 4,
      zastupnyText: "napríklad 2026",
    },
  ];

  const zmenFilter = (kluc, hodnota) => {
    setFiltre((predosle) => ({ ...predosle, [kluc]: hodnota }));
    setPageNumberToGrid(1);
    setLoadingList(true);
  };

  const vycistiFiltre = () => {
    setFiltre({
      branchOfficeId: undefined,
      carOwnerId: undefined,
      carRenterId: undefined,
      obdobie: undefined,
      suma: undefined,
      cisloFaktury: undefined,
    });
    setPageNumberToGrid(1);
    setLoadingList(true);
  };

  const buttons = (
    <>
      <Button
        onClick={exportBlAction}
        className="btn-add_new"
        type="primary"
        style={{ marginLeft: "10px" }}
      >
        <Link to="#">BL Export</Link>
      </Button>
      <Button
        onClick={showNewRecordModal}
        className="btn-add_new"
        type="primary"
        style={{ marginLeft: "10px" }}
      >
        <Link to="#">{t("common.addRecord")}</Link>
      </Button>
    </>
  );
  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t("backend.actions.title")}
          subTitle={
            <>
              <span className="title-counter">{t("carOwners.filter")} </span>
              <AutoComplete
                onSearch={handleSearch}
                // dataSource={notData}
                placeholder={t("carOwners.startWritting")}
                patterns
              />
              <Button
                onClick={() => setSearchText("")}
                className="btn-clear-search"
                size="default"
                type="default"
              >
                Vymazať hľadanie
              </Button>

              {/* Stavy boli pod sebou: kazdy checkbox mal obal s triedami
                  col-sm-12 px-3 py-2, ale Bootstrap v projekte nie je,
                  takze z toho ostal obycajny blok na cely riadok. */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "14px",
                  marginLeft: "14px",
                }}
              >
                {STAVY_ZAKAZIEK.map((item) => {
                  return (
                    <Checkbox
                      key={item.label}
                      onChange={onChangeCheckbox}
                      checked={item.value === value}
                      value={item.value}
                      name={item.name}
                    >
                      {item.value}
                    </Checkbox>
                  );
                })}
              </div>
            </>
          }
        />
      </CardToolbox>
      <Main>
        <Row gutter={15}>
          <Col md={24}>
            <PanelFiltrov
              filtre={definicieFiltrov}
              hodnoty={filtre}
              onZmena={zmenFilter}
              onVycistit={vycistiFiltre}
            />
          </Col>
        </Row>
        <Row gutter={15}>
          <Col md={24}>
            {/* Spinner len pri prvom nacitani, potom zoznam zostava. */}
            {loadingList && total === 0 && (
              <div className="spin">
                <Spin />
              </div>
            )}
            {(!loadingList || total > 0) && (
              <List
                loading={loadingList}
                inputData={listData}
                graphqlVariables={graphqlVariables}
                checkedStatus={checkedStatus}
                total={total}
                sendPageNumberToGrid={handleChangePage}
                pageNumberToGrid={pageNumberToGrid}
                buttons={buttons}
                limit={limit}
                downloadCsvStatus={handleDownloadCsv}
                premenneDoExportu={queryVariables}
                onPoFakturacii={() => {
                  if (typeof graphQlData?.[0]?.refetch === "function") {
                    graphQlData[0].refetch();
                  }
                }}
              />
            )}
          </Col>
        </Row>
      </Main>
      <NewRecordModal
        t={t}
        gridState={gridState}
        handleCancel={handleCancel}
        onCancel={onCancel}
        tableData={tableData}
        branchOffices={branchOficcesData}
      />
      {/* <NewRecordModal
        t={t}
        gridState={gridState}
        handleCancel={handleCancel}
        onCancel={onCancel}
        tableData={tableData}
        branchOffices={branchOficcesData}
      /> */}
    </>
  );
};

export default Actions;
